import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import type { NoopT } from 'declarations/types/common';
import usePushNotifications from 'hooks/common/usePushNotifications';

export interface TicketsPageHookProps {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
}

export type SelectTicketPayloadT = {
  roNumber: string;
  lineNumber: string | null;
};

type UseTicketsPageT = {
  hasNextTicketsPage: boolean | undefined;
  handleSelectTicket: (ticketId: string, payload: SelectTicketPayloadT) => NoopT;
};

const useTicketsPage = (
  { ticketsPages: _ticketsPages }: TicketsPageHookProps = { ticketsPages: null }
): UseTicketsPageT => {
  const {
    setTicketId,
    hasNextTicketsPage,
    setOpenedTicketTab,
    setOpenedScheduleTicketTab,
    setActiveTab,
    activeTab,
    saveTicketListScrollPosition,
  } = useTickets();
  const location = useLocation();
  const navigate = useNavigate();
  usePushNotifications();

  const handleSelectTicket = useCallback(
    (ticketId: string, payload: SelectTicketPayloadT) => (): void => {
      const tabLabel = payload.lineNumber
        ? `${payload.roNumber} - ${payload.lineNumber}`
        : payload.roNumber;
      const isFromScheduleList = activeTab === ActiveTabTypeE.Schedule;
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('section');
      searchParams.delete('tabLabel');
      if (isFromScheduleList) {
        searchParams.set('section', 'schedule');
        searchParams.set('tabLabel', tabLabel);
      }
      const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
      const path = `${RoutePathsE.TICKET_DETAIL}/${ticketId}${search}`;

      if (isFromScheduleList) {
        setOpenedScheduleTicketTab(tabLabel, ticketId);
        setActiveTab(ActiveTabTypeE.Schedule);
        sessionStorage.setItem('activeTab', ActiveTabTypeE.Schedule);
      } else {
        setActiveTab(ActiveTabTypeE.Tickets);
        sessionStorage.setItem('activeTab', ActiveTabTypeE.Tickets);
        setOpenedTicketTab(tabLabel, ticketId);
      }
      saveTicketListScrollPosition();
      setTicketId(ticketId);
      navigate(path);
    },
    [
      activeTab,
      location.search,
      setOpenedTicketTab,
      setOpenedScheduleTicketTab,
      setActiveTab,
      setTicketId,
      navigate,
      saveTicketListScrollPosition,
    ]
  );

  return {
    handleSelectTicket,
    hasNextTicketsPage,
  };
};

export default useTicketsPage;
